import { ComponentProps, JSX, JSXElementConstructor } from 'react';
import { CdaStructuredTextValue, renderNodeRule, StructuredText } from 'react-datocms/structured-text';
import Markdown from 'react-markdown';
import clsx from 'clsx';
import BlockItem, { Block } from 'components/Blocks';
import useMarketingUrl from 'hooks/useMarketingUrl';

import styles from './RichContent.module.css';

interface Props extends Omit<ComponentProps<'div'>, 'children'> {
  children: string | CdaStructuredTextValue<Block>;
  as?: keyof JSX.IntrinsicElements | JSXElementConstructor<any>;
  disableContentLink?: boolean;
  align?: 'center' | 'left' | 'right';
}

export default function RichContent({
  children,
  as: Tag = 'div',
  className,
  align,
  disableContentLink,
  ...props
}: Props) {
  const getUrl = useMarketingUrl();

  return (
    <Tag
      className={clsx(
        styles.content,
        typeof Tag === 'string' && styles[Tag],
        align && styles[`alignement-${align}`],
        className,
      )}
      data-datocms-content-link-group={disableContentLink ? undefined : true}
      data-richcontent
      {...props}
    >
      {typeof children === 'string' ? (
        <Markdown
          components={{
            a: ({ node, ...props }) => (
              <a
                {...props}
                className={clsx(styles.link, props.className)}
                target="_blank"
                rel="noreferrer nofollow"
                href={props.href ? getUrl(props.href) : undefined}
              />
            ),
          }}
        >
          {children}
        </Markdown>
      ) : (
        <StructuredText
          data={children}
          data-richcontent
          renderBlock={({ record }) => (
            <div className={styles.blockItem}>
              <BlockItem
                key={record.id}
                isContained
                {...record}
              />
            </div>
          )}
          renderInlineBlock={({ record }) => (
            <BlockItem
              key={record.id}
              {...record}
            />
          )}
          renderLinkToRecord={({ children, transformedMeta: props, record }) =>
            'slug' in record ? (
              <a
                href={getUrl(record.slug as string)}
                className={styles.link}
                {...props}
              >
                {children}
              </a>
            ) : (
              <>{children}</>
            )
          }
          customNodeRules={[
            renderNodeRule(
              (node) => node.type === 'link',
              ({ children, key, node }) => (
                <a
                  className={styles.link}
                  key={key}
                  href={getUrl(node.url)}
                  target="_blank"
                  rel="noreferrer nofollow"
                >
                  {children}
                </a>
              ),
            ),
          ]}
        />
      )}
    </Tag>
  );
}
